import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import "@/App.css";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import {
  Factory, MagnifyingGlass, X, ArrowsClockwise, DownloadSimple, UploadSimple, Plus,
} from "@phosphor-icons/react";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { DateField } from "@/components/DateField";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 2500;

// `site_id` repeats once per selected site - FastAPI's List[str] Query
// parses ?site_id=P1&site_id=P7 (NOT the bracketed ?site_id[]=... some
// serializers default to), so build this by hand rather than trust
// axios's own array-param encoding.
const siteParams = (sites) => {
  const p = new URLSearchParams();
  sites.forEach((s) => p.append("site_id", s));
  return p;
};

const StatCard = ({ label, value, testId }) => (
  <div className="bg-white border border-[#D0D5DD] rounded-md px-4 py-3" data-testid={testId}>
    <div className="text-xs text-[#667085] font-sans">{label}</div>
    <div className="text-xl font-bold text-[#1D2939] font-heading mt-0.5">{value}</div>
  </div>
);

// Indian numbering system (lakh/crore grouping, e.g. 54,65,167.92) - user's
// explicit ask, this screen's values are INR business figures for an
// India-based SAP tenant, so group them the way users here actually read
// them rather than whatever locale the browser happens to report.
const fmtNum = (n) => (n === null || n === undefined ? "\u2014" : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 3 }));
const fmtMoney = (n, ccy) => (n === null || n === undefined ? "\u2014" : `${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${ccy ? ` ${ccy}` : ""}`);

// Feb 2026, user's explicit ask: "Create a new page: Monthly Consumption
// Monitor" - a persistent per-site watchlist of finished-goods products,
// an (interim, manual-upload) monthly Sales + Service quantity per FG, and
// a live-computed, then-cached monthly consumption value report: the
// Component Consumption tab explodes every watchlisted FG's BOM (same
// pipeline Purchasing Plan already uses) to get each raw component's
// monthly consumption x its own live SAP Moving Average price; the FG
// Sale Value tab is simpler - each FG's own monthly qty x its own Moving
// Average price, kept in a separate tab per the user's explicit ask.
export default function FgConsumptionMonitorPage() {
  const [sites, setSites] = useState([]);
  // Feb 2026, user's explicit ask ("instead of a dropdown, give me
  // checkboxes at the top. Allow multi-site check selection") - replaces
  // the old single siteId + special ALL_SITES sentinel with an actual
  // array of 1+ selected real sites; selecting several merges exactly
  // those (same union math the old "All Sites" option always used,
  // just no longer tied to literally every site).
  const [selectedSites, setSelectedSites] = useState([]);

  const [watchlist, setWatchlist] = useState([]);
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [adding, setAdding] = useState(null);
  const [removing, setRemoving] = useState(null);
  const debounceRef = useRef(null);
  const requestRef = useRef(null);

  const [salesStatus, setSalesStatus] = useState({});
  const [salesFile, setSalesFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [componentSearch, setComponentSearch] = useState("");
  const [fgSearch, setFgSearch] = useState("");
  // Feb 2026, user's explicit ask ("wire up a real attempt against
  // SalesOrderCollection...that would tell us definitively which path we
  // need") - Monthly Sales Qty now pulls live from real, invoiced SAP
  // Sales Orders. Follow-up ask ("allow setting a sales window") - the
  // period is a free-form start/end date range, not locked to a calendar
  // month. Defaults to the most recently completed month (fetched once
  // on mount); changing it always triggers a live recompute.
  const [periodStart, setPeriodStart] = useState(null);
  const [periodEnd, setPeriodEnd] = useState(null);
  // Guards against two known race conditions when the site selection is
  // changed mid-flight (testing agent finding, Feb 2026): generationTokenRef
  // makes only the LATEST generateReport call (whether triggered by a
  // selection change's cache-miss or an explicit Refresh click) ever
  // apply its result; siteLoadTokenRef does the same for the
  // cached-report lookup itself, so a slow response for a selection the
  // user has already left never overwrites what's on screen for the
  // selection they're now looking at.
  const generationTokenRef = useRef(0);
  const siteLoadTokenRef = useRef(0);

  useEffect(() => {
    axios.get(`${API}/fg-consumption/sites`).then(({ data }) => {
      const list = data.sites || [];
      setSites(list);
      if (list.length) setSelectedSites([list[0]]);
    }).catch((err) => toast.error("Could not load sites", { description: err?.response?.data?.detail || err.message }));
  }, []);

  const loadWatchlist = useCallback(async (siteIds, myToken) => {
    if (!siteIds.length) return;
    setWatchlistLoading(true);
    try {
      const { data } = await axios.get(`${API}/fg-consumption/watchlist`, { params: siteParams(siteIds) });
      if (myToken === undefined || siteLoadTokenRef.current === myToken) setWatchlist(data.items || []);
    } catch (err) {
      if (myToken === undefined || siteLoadTokenRef.current === myToken) {
        toast.error("Could not load watchlist", { description: err?.response?.data?.detail || err.message });
      }
    } finally {
      if (myToken === undefined || siteLoadTokenRef.current === myToken) setWatchlistLoading(false);
    }
  }, []);

  const loadSalesStatus = useCallback(async (siteIds, myToken) => {
    if (!siteIds.length) return;
    try {
      const { data } = await axios.get(`${API}/fg-consumption/sales-data/status`, { params: siteParams(siteIds) });
      if (myToken === undefined || siteLoadTokenRef.current === myToken) setSalesStatus(data || {});
    } catch {
      if (myToken === undefined || siteLoadTokenRef.current === myToken) setSalesStatus({});
    }
  }, []);

  const loadCachedReport = useCallback(async (siteIds) => {
    if (!siteIds.length) return null;
    const { data } = await axios.get(`${API}/fg-consumption/report/latest`, { params: siteParams(siteIds) });
    return data.report || null;
  }, []);

  // Testing agent finding (Feb 2026, HIGH): stop polling the instant this
  // job's own token goes stale (the user moved to a different selection
  // mid-poll) - otherwise an abandoned job keeps polling every 2.5s and
  // keeps the live SAP computation running on the server for a selection
  // nobody's looking at anymore.
  const pollReportJob = async (jobId, myToken) => {
    for (;;) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      if (generationTokenRef.current !== myToken) return null;
      const { data } = await axios.get(`${API}/fg-consumption/report/status/${jobId}`);
      if (data.status === "done") return data.result;
      if (data.status === "failed") throw new Error(data.error || "Report generation failed");
    }
  };

  const generateReport = useCallback(async (siteIds, startDate, endDate) => {
    if (!siteIds.length) return;
    const myToken = ++generationTokenRef.current;
    setGenerating(true);
    setElapsedSeconds(0);
    const timer = setInterval(() => {
      if (generationTokenRef.current === myToken) setElapsedSeconds((s) => s + 1);
    }, 1000);
    try {
      const params = siteParams(siteIds);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      const { data } = await axios.post(`${API}/fg-consumption/report/generate`, null, { params });
      const result = await pollReportJob(data.job_id, myToken);
      if (generationTokenRef.current === myToken && result) {
        setReport(result);
        if (result?.period) {
          setPeriodStart(result.period.start_date);
          setPeriodEnd(result.period.end_date);
        }
        toast.success("Report generated", { description: `Computed live against SAP for ${siteIds.length > 1 ? `${siteIds.length} sites (${siteIds.join(", ")})` : siteIds[0]}.` });
      }
    } catch (err) {
      if (generationTokenRef.current === myToken) {
        toast.error("Report generation failed", { description: err?.response?.data?.detail || err.message });
      }
    } finally {
      clearInterval(timer);
      if (generationTokenRef.current === myToken) setGenerating(false);
    }
  }, []);

  useEffect(() => {
    axios.get(`${API}/fg-consumption/report/default-window`).then(({ data }) => {
      // Testing agent finding (Feb 2026, MEDIUM): only seed the picker if
      // nothing has set it yet. Otherwise this mount-time fetch can
      // resolve AFTER a cached report has already synced the picker to
      // its own window, silently overriding it with the wrong dates.
      setPeriodStart((p) => p ?? data.start_date);
      setPeriodEnd((p) => p ?? data.end_date);
    }).catch(() => {});
  }, []);

  // Selection change: reload everything for the newly selected site(s).
  // "Live 1st time, then cached" - a cache miss means this exact
  // combination has never had a report generated, so kick off the live
  // computation automatically; an existing cache just loads instantly,
  // and the Refresh button is the only thing that forces a live
  // recompute after that. Keyed on the sorted, joined selection rather
  // than the array reference itself, so toggling a checkbox back to the
  // same set of sites doesn't re-trigger this.
  //
  // Testing agent finding (Feb 2026, HIGH): bumping siteLoadTokenRef
  // alone wasn't enough - a LIVE GENERATION started for the selection
  // the user just left was still "current" per generationTokenRef, so
  // it would finish minutes later and silently overwrite whatever
  // cached report the new selection had already loaded. Every selection
  // change now also bumps generationTokenRef itself (invalidating any
  // in-flight generation outright) and clears `generating`, so only a
  // generation started for the CURRENT selection can ever call
  // setReport().
  const selectedSitesKey = [...selectedSites].sort().join(",");
  useEffect(() => {
    if (!selectedSites.length) return;
    const myToken = ++siteLoadTokenRef.current;
    generationTokenRef.current += 1;
    setGenerating(false);
    setWatchlist([]);
    loadWatchlist(selectedSites, myToken);
    loadSalesStatus(selectedSites, myToken);
    setReport(null);
    setReportLoading(true);
    loadCachedReport(selectedSites).then((cached) => {
      if (siteLoadTokenRef.current !== myToken) return;
      setReportLoading(false);
      if (cached) {
        setReport(cached);
        // Sync the picker to whatever window this cached report actually
        // covers, so it never silently disagrees with what's on screen.
        if (cached.period?.start_date) {
          setPeriodStart(cached.period.start_date);
          setPeriodEnd(cached.period.end_date);
        }
      } else {
        generateReport(selectedSites, periodStart, periodEnd);
      }
    }).catch((err) => {
      if (siteLoadTokenRef.current !== myToken) return;
      setReportLoading(false);
      toast.error("Could not load report", { description: err?.response?.data?.detail || err.message });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSitesKey]);

  const toggleSite = (site) => {
    setSelectedSites((prev) => {
      if (prev.includes(site)) {
        // Always leave at least one site selected - an empty selection
        // has nothing meaningful to show.
        return prev.length === 1 ? prev : prev.filter((s) => s !== site);
      }
      return [...prev, site];
    });
  };

  const allSitesSelected = sites.length > 0 && selectedSites.length === sites.length;
  const toggleAllSites = () => setSelectedSites(allSitesSelected ? [sites[0]] : [...sites]);

  const singleSite = selectedSites.length === 1 ? selectedSites[0] : null;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2 || !singleSite) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const requestId = q;
      requestRef.current = requestId;
      try {
        const { data } = await axios.get(`${API}/products/search`, { params: { q, limit: 10 } });
        if (requestRef.current === requestId) setSuggestions(data);
      } catch {
        if (requestRef.current === requestId) setSuggestions([]);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, singleSite]);

  const trackedIds = new Set(watchlist.map((w) => w.product_id));

  const addProduct = async (item) => {
    if (!singleSite) return;
    setAdding(item.product_id);
    try {
      await axios.post(`${API}/fg-consumption/watchlist`, { site_id: singleSite, product_id: item.product_id, description: item.description });
      toast.success(`${item.product_id} added to ${singleSite}'s watchlist`);
      setQuery("");
      setSuggestions([]);
      await loadWatchlist(selectedSites);
    } catch (err) {
      toast.error("Could not add product", { description: err?.response?.data?.detail || err.message });
    } finally {
      setAdding(null);
    }
  };

  const removeProduct = async (entry) => {
    setRemoving(entry.id);
    try {
      await axios.delete(`${API}/fg-consumption/watchlist/${entry.id}`);
      toast.success(`${entry.product_id} removed`);
      await loadWatchlist(selectedSites);
    } catch (err) {
      toast.error("Could not remove", { description: err?.response?.data?.detail || err.message });
    } finally {
      setRemoving(null);
    }
  };

  const uploadSalesData = async () => {
    if (!salesFile || !singleSite) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", salesFile);
      const { data } = await axios.post(`${API}/fg-consumption/sales-data/upload`, formData, { params: { site_id: singleSite } });
      toast.success(
        `Uploaded ${data.rows_stored} row(s) for ${singleSite}`,
        data.rows_skipped ? { description: `Skipped ${data.rows_skipped} row(s) with an invalid quantity.` } : undefined,
      );
      setSalesFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await loadSalesStatus(selectedSites);
    } catch (err) {
      toast.error("Upload failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setUploading(false);
    }
  };

  const componentRows = useMemo(() => {
    const rows = report?.component_rows || [];
    const q = componentSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.product_id.toLowerCase().includes(q) || (r.description || "").toLowerCase().includes(q));
  }, [report, componentSearch]);

  const fgRows = useMemo(() => {
    const rows = report?.fg_rows || [];
    const q = fgSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.product_id.toLowerCase().includes(q) || (r.description || "").toLowerCase().includes(q));
  }, [report, fgSearch]);

  const isCombinedView = !singleSite;
  const currentSalesStatus = singleSite ? salesStatus[singleSite] : null;

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      <div className="bg-[#0E7C86] px-4 sm:px-6 py-3 flex items-center gap-3">
        <Factory size={22} weight="bold" className="text-white shrink-0" />
        <span className="text-white font-heading font-bold text-base shrink-0">Materials Hub</span>
        <NavTabs />
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#1D2939] font-heading" data-testid="fg-consumption-monitor-title">
              Monthly Consumption Monitor
            </h1>
            <p className="text-sm text-[#667085] font-sans mt-0.5">
              Track finished goods, pull live SAP Sales Qty + manual Service Qty for any sales window, and see
              component-level and finished-good inventory value at live SAP Moving Average price.
            </p>
          </div>
        </div>

        {/* Feb 2026, user's explicit ask: "instead of a dropdown, give me
            checkboxes at the top. Allow multi-site check selection" -
            picking 2+ boxes merges exactly those sites into one combined
            view (same union math "All Sites" always did); at least one
            box is always kept checked. */}
        <div className="bg-white border border-[#D0D5DD] rounded-md px-4 py-3 mb-4 flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="fg-consumption-monitor-site-checkboxes">
          <span className="text-xs font-bold text-[#344054] shrink-0">Sites</span>
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <Checkbox
              checked={allSitesSelected}
              onCheckedChange={toggleAllSites}
              data-testid="fg-consumption-monitor-site-checkbox-all"
            />
            <span className="text-sm text-[#1D2939] font-sans font-semibold">All</span>
          </label>
          <span className="h-4 w-px bg-[#EAECF0] shrink-0" />
          {sites.map((s) => (
            <label key={s} className="flex items-center gap-1.5 cursor-pointer select-none" data-testid={`fg-consumption-monitor-site-checkbox-label-${s}`}>
              <Checkbox
                checked={selectedSites.includes(s)}
                onCheckedChange={() => toggleSite(s)}
                data-testid={`fg-consumption-monitor-site-checkbox-${s}`}
              />
              <span className="text-sm text-[#1D2939] font-sans">{s}</span>
            </label>
          ))}
        </div>

        {/* ---------------- Watchlist ---------------- */}
        <div className="bg-white border border-[#D0D5DD] rounded-md p-4 mb-5">
          <h2 className="font-heading text-sm font-bold text-[#1D2939] mb-1">
            Tracked Finished Goods {isCombinedView ? `(union across ${selectedSites.join(", ")})` : `- ${singleSite}`}
          </h2>
          <p className="text-xs text-[#667085] font-sans mb-3">
            {isCombinedView
              ? "Read-only combined view - check exactly one site above to add or remove a product."
              : "Products added here stay tracked indefinitely until removed - keep adding as needed."}
          </p>

          {!isCombinedView && (
            <div className="relative mb-3">
              <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#98A2B3]" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search finished good Product ID or description to add"
                className="pl-9 h-9"
                data-testid="fg-consumption-monitor-search-input"
              />
              {suggestions.length > 0 && (
                <div className="mt-2 border border-[#EAECF0] rounded-md divide-y divide-[#F2F4F7] absolute bg-white w-full z-10 shadow-md" data-testid="fg-consumption-monitor-suggestions">
                  {suggestions.map((s) => (
                    <div key={s.product_id} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50" data-testid={`fg-consumption-monitor-suggestion-${s.product_id}`}>
                      <div>
                        <div className="font-mono text-sm font-semibold text-[#1D2939]">{s.product_id}</div>
                        {s.description && <div className="text-xs text-[#667085]">{s.description}</div>}
                      </div>
                      <Button
                        size="sm"
                        className="gap-1 bg-[#0E7C86] hover:bg-[#0B6B74]"
                        disabled={trackedIds.has(s.product_id) || adding === s.product_id}
                        onClick={() => addProduct(s)}
                        data-testid={`fg-consumption-monitor-add-button-${s.product_id}`}
                      >
                        <Plus size={13} weight="bold" />
                        {trackedIds.has(s.product_id) ? "Already tracked" : adding === s.product_id ? "Adding..." : "Add"}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="border border-[#EAECF0] rounded-md overflow-x-auto max-h-64 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-[#EAECF0] text-left text-xs text-[#667085] font-sans">
                  <th className="px-3 py-2 font-medium">Product ID</th>
                  <th className="px-3 py-2 font-medium">Description</th>
                  {isCombinedView && <th className="px-3 py-2 font-medium">Site(s)</th>}
                  {!isCombinedView && <th className="px-3 py-2 font-medium text-right">Action</th>}
                </tr>
              </thead>
              <tbody>
                {watchlistLoading && (
                  <tr><td colSpan={3} className="px-3 py-6 text-center text-[#667085] text-sm" data-testid="fg-consumption-monitor-watchlist-loading">Loading...</td></tr>
                )}
                {!watchlistLoading && watchlist.length === 0 && (
                  <tr><td colSpan={3} className="px-3 py-6 text-center text-[#667085] text-sm" data-testid="fg-consumption-monitor-watchlist-empty">No finished goods tracked yet.</td></tr>
                )}
                {watchlist.map((w) => (
                  <tr key={w.id || w.product_id} className="border-b border-[#F2F4F7] hover:bg-slate-50" data-testid={`fg-consumption-monitor-watchlist-row-${w.product_id}`}>
                    <td className="px-3 py-2 font-mono font-semibold text-[#1D2939]">{w.product_id}</td>
                    <td className="px-3 py-2 text-[#475467]">{w.description || "\u2014"}</td>
                    {isCombinedView && <td className="px-3 py-2 text-[#475467]">{(w.sites || []).join(", ")}</td>}
                    {!isCombinedView && (
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1.5 text-[#B42318] border-[#FDA29B] hover:bg-[#FEF3F2]"
                          disabled={removing === w.id}
                          onClick={() => removeProduct(w)}
                          data-testid={`fg-consumption-monitor-remove-button-${w.product_id}`}
                        >
                          <X size={13} weight="bold" />
                          {removing === w.id ? "Removing..." : "Remove"}
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ---------------- Service Data ---------------- */}
        <div className="bg-white border border-[#D0D5DD] rounded-md p-4 mb-5">
          <h2 className="font-heading text-sm font-bold text-[#1D2939] mb-1">Service Data</h2>
          <p className="text-xs text-[#667085] font-sans mb-3" data-testid="fg-consumption-monitor-sales-source-note">
            Monthly Sales Qty is now pulled live from real, invoiced SAP Sales Orders for the sales window selected below - no
            upload needed. This upload is only for Monthly Service Qty (no live SAP source for that yet). Product ID,
            Monthly Service Qty - uploading replaces the previous data for that site.
          </p>

          {isCombinedView ? (
            <div className="border border-[#EAECF0] rounded-md overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[#EAECF0] text-left text-[#667085] font-sans">
                    <th className="px-3 py-2 font-medium">Site</th>
                    <th className="px-3 py-2 font-medium">Products with data</th>
                    <th className="px-3 py-2 font-medium">Last uploaded</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(salesStatus).length === 0 && (
                    <tr><td colSpan={3} className="px-3 py-6 text-center text-[#667085] text-sm" data-testid="fg-consumption-monitor-sales-status-empty">No Service data uploaded anywhere yet.</td></tr>
                  )}
                  {Object.entries(salesStatus).map(([site, s]) => (
                    <tr key={site} className="border-b border-[#F2F4F7]" data-testid={`fg-consumption-monitor-sales-status-row-${site}`}>
                      <td className="px-3 py-2 font-semibold text-[#1D2939]">{site}</td>
                      <td className="px-3 py-2 text-[#475467]">{s.row_count}</td>
                      <td className="px-3 py-2 text-[#475467]">{new Date(s.uploaded_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <a
                href={`${API}/fg-consumption/sales-data/template.xlsx`}
                className="inline-flex items-center justify-center gap-1.5 h-9 px-3 text-xs font-bold text-[#004B87] border border-[#D0D5DD] rounded-sm hover:bg-[#F9FAFB] w-fit"
                data-testid="fg-consumption-monitor-sales-template-link"
              >
                <DownloadSimple size={14} weight="bold" />
                Download Template
              </a>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                onChange={(e) => setSalesFile(e.target.files?.[0] || null)}
                className="text-xs flex-1"
                data-testid="fg-consumption-monitor-sales-file-input"
              />
              <Button
                size="sm"
                className="gap-1.5 bg-[#0E7C86] hover:bg-[#0B6B74]"
                disabled={!salesFile || uploading}
                onClick={uploadSalesData}
                data-testid="fg-consumption-monitor-sales-upload-button"
              >
                <UploadSimple size={14} weight="bold" />
                {uploading ? "Uploading..." : "Upload"}
              </Button>
              <span className="text-xs text-[#667085] font-sans ml-auto" data-testid="fg-consumption-monitor-sales-status">
                {currentSalesStatus
                  ? `${currentSalesStatus.row_count} product(s), last uploaded ${new Date(currentSalesStatus.uploaded_at).toLocaleString()}`
                  : "No data uploaded for this site yet."}
              </span>
            </div>
          )}
        </div>

        {/* ---------------- Report ---------------- */}
        <div className="bg-white border border-[#D0D5DD] rounded-md p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <div>
              <h2 className="font-heading text-sm font-bold text-[#1D2939]">Monthly Consumption Report</h2>
              <p className="text-xs text-[#667085] font-sans mt-0.5" data-testid="fg-consumption-monitor-generated-at">
                {report?.generated_at
                  ? `Last generated ${new Date(report.generated_at).toLocaleString()} for ${report.period?.start_date ? `${format(parseISO(report.period.start_date), "dd-MM-yyyy")} to ${format(parseISO(report.period.end_date), "dd-MM-yyyy")}` : "\u2014"}`
                  : "Not generated yet."}
              </p>
            </div>
            <div className="flex items-start gap-2 flex-wrap">
              <div>
                <span className="block text-xs font-bold text-[#344054]">Sales Window From</span>
                <DateField
                  value={periodStart}
                  onChange={setPeriodStart}
                  testId="fg-consumption-monitor-period-start-trigger"
                  className="w-36"
                  disabledMatcher={periodEnd ? (date) => date > parseISO(periodEnd) : undefined}
                />
              </div>
              <div>
                <span className="block text-xs font-bold text-[#344054]">To</span>
                <DateField
                  value={periodEnd}
                  onChange={setPeriodEnd}
                  testId="fg-consumption-monitor-period-end-trigger"
                  className="w-36"
                  disabledMatcher={periodStart ? (date) => date < parseISO(periodStart) : undefined}
                />
              </div>
              <Button
                className="gap-2 bg-[#0E7C86] hover:bg-[#0B6B74] w-fit h-9 mt-[21px]"
                onClick={() => generateReport(selectedSites, periodStart, periodEnd)}
                disabled={generating || reportLoading || !periodStart || !periodEnd}
                data-testid="fg-consumption-monitor-generate-button"
              >
                <ArrowsClockwise size={15} weight="bold" className={generating ? "animate-spin" : ""} />
                {generating ? `Generating... (${elapsedSeconds}s)` : "Refresh Report (Live)"}
              </Button>
            </div>
          </div>

          {(reportLoading || generating) && (
            <div className="bg-[#EFF8FF] border border-[#B2DDFF] text-[#175CD3] text-sm rounded-md px-4 py-3 mb-4" data-testid="fg-consumption-monitor-generating-banner">
              {generating
                ? "Exploding each tracked FG's BOM and fetching live SAP Sales Orders + Moving Average prices - this can take a little while on a large or never-before-explored watchlist."
                : "Loading..."}
            </div>
          )}

          {report && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <StatCard label="Tracked FG Products" value={report.watchlist_count} testId="fg-consumption-monitor-stat-watchlist" />
                <StatCard label="Component Consumption Value" value={fmtMoney(report.component_total_value, report.currency)} testId="fg-consumption-monitor-stat-component-value" />
                <StatCard label="FG Sale Value" value={fmtMoney(report.fg_total_value, report.currency)} testId="fg-consumption-monitor-stat-fg-value" />
                <StatCard label="FGs Without a BOM" value={(report.unresolved_fg || []).length} testId="fg-consumption-monitor-stat-unresolved" />
              </div>

              <Tabs defaultValue="components">
                <TabsList>
                  <TabsTrigger value="components" data-testid="fg-consumption-monitor-tab-components">Component Consumption</TabsTrigger>
                  <TabsTrigger value="fg" data-testid="fg-consumption-monitor-tab-fg">FG Sale Value</TabsTrigger>
                </TabsList>

                <TabsContent value="components">
                  <div className="flex flex-wrap items-center gap-2 my-3">
                    <div className="relative flex-1 min-w-[220px] max-w-sm">
                      <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#98A2B3]" />
                      <Input
                        value={componentSearch}
                        onChange={(e) => setComponentSearch(e.target.value)}
                        placeholder="Search component code or description"
                        className="pl-9 h-9"
                        data-testid="fg-consumption-monitor-component-search-input"
                      />
                    </div>
                    <span className="text-xs text-[#475467] ml-auto font-sans" data-testid="fg-consumption-monitor-component-count">
                      {componentRows.length.toLocaleString()} of {(report.component_rows || []).length.toLocaleString()} rows
                    </span>
                    <a
                      href={`${API}/fg-consumption/report/download.xlsx?${siteParams(selectedSites).toString()}`}
                      className="inline-flex items-center gap-1.5 h-9 px-3 text-xs font-bold text-[#004B87] border border-[#D0D5DD] rounded-sm hover:bg-[#F9FAFB]"
                      data-testid="fg-consumption-monitor-download-button"
                    >
                      <DownloadSimple size={14} weight="bold" />
                      Download Excel
                    </a>
                  </div>
                  <div className="border border-[#EAECF0] rounded-md overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-[#EAECF0] text-left text-[#667085] font-sans">
                          <th className="px-3 py-2 font-medium">Component</th>
                          <th className="px-3 py-2 font-medium">Description</th>
                          <th className="px-3 py-2 font-medium">UOM</th>
                          <th className="px-3 py-2 font-medium text-right">Window Qty</th>
                          <th className="px-3 py-2 font-medium text-right">Unit Price</th>
                          <th className="px-3 py-2 font-medium text-right">Window Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {componentRows.length === 0 && (
                          <tr><td colSpan={6} className="px-3 py-6 text-center text-[#667085] text-sm" data-testid="fg-consumption-monitor-components-empty">No component rows.</td></tr>
                        )}
                        {componentRows.map((r) => (
                          <tr key={r.product_id} className="border-b border-[#F2F4F7] hover:bg-slate-50" data-testid={`fg-consumption-monitor-component-row-${r.product_id}`}>
                            <td className="px-3 py-2 font-mono font-semibold text-[#1D2939]">{r.product_id}</td>
                            <td className="px-3 py-2 text-[#475467]">{r.description || "\u2014"}</td>
                            <td className="px-3 py-2 text-[#475467]">{r.unit_of_measure || "\u2014"}</td>
                            <td className="px-3 py-2 text-right">{fmtNum(r.monthly_qty)}</td>
                            <td className="px-3 py-2 text-right">{fmtMoney(r.unit_price, r.currency)}</td>
                            <td className="px-3 py-2 text-right font-semibold">{fmtMoney(r.monthly_value, r.currency)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>

                <TabsContent value="fg">
                  <div className="flex flex-wrap items-center gap-2 my-3">
                    <div className="relative flex-1 min-w-[220px] max-w-sm">
                      <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#98A2B3]" />
                      <Input
                        value={fgSearch}
                        onChange={(e) => setFgSearch(e.target.value)}
                        placeholder="Search FG product ID or description"
                        className="pl-9 h-9"
                        data-testid="fg-consumption-monitor-fg-search-input"
                      />
                    </div>
                    <span className="text-xs text-[#475467] ml-auto font-sans" data-testid="fg-consumption-monitor-fg-count">
                      {fgRows.length.toLocaleString()} of {(report.fg_rows || []).length.toLocaleString()} rows
                    </span>
                    <a
                      href={`${API}/fg-consumption/report/download.xlsx?${siteParams(selectedSites).toString()}`}
                      className="inline-flex items-center gap-1.5 h-9 px-3 text-xs font-bold text-[#004B87] border border-[#D0D5DD] rounded-sm hover:bg-[#F9FAFB]"
                      data-testid="fg-consumption-monitor-fg-download-button"
                    >
                      <DownloadSimple size={14} weight="bold" />
                      Download Excel
                    </a>
                  </div>
                  <div className="border border-[#EAECF0] rounded-md overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-[#EAECF0] text-left text-[#667085] font-sans">
                          <th className="px-3 py-2 font-medium">FG Product</th>
                          <th className="px-3 py-2 font-medium">Description</th>
                          <th className="px-3 py-2 font-medium text-right">Sales Qty (Live)</th>
                          <th className="px-3 py-2 font-medium text-right">Service Qty</th>
                          <th className="px-3 py-2 font-medium text-right">Unit Price</th>
                          <th className="px-3 py-2 font-medium text-right">Window Value</th>
                          <th className="px-3 py-2 font-medium">Flags</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fgRows.length === 0 && (
                          <tr><td colSpan={7} className="px-3 py-6 text-center text-[#667085] text-sm" data-testid="fg-consumption-monitor-fg-rows-empty">No FG rows.</td></tr>
                        )}
                        {fgRows.map((r) => (
                          <tr key={r.product_id} className="border-b border-[#F2F4F7] hover:bg-slate-50" data-testid={`fg-consumption-monitor-fg-row-${r.product_id}`}>
                            <td className="px-3 py-2 font-mono font-semibold text-[#1D2939]">{r.product_id}</td>
                            <td className="px-3 py-2 text-[#475467]">{r.description || "\u2014"}</td>
                            <td className="px-3 py-2 text-right">{fmtNum(r.monthly_sales_qty)}</td>
                            <td className="px-3 py-2 text-right">{fmtNum(r.monthly_service_qty)}</td>
                            <td className="px-3 py-2 text-right">{fmtMoney(r.unit_price, r.currency)}</td>
                            <td className="px-3 py-2 text-right font-semibold">{fmtMoney(r.monthly_value, r.currency)}</td>
                            <td className="px-3 py-2">
                              {!r.has_bom && <span className="text-[10px] font-bold text-[#B42318] bg-[#FEF3F2] border border-[#FDA29B] rounded px-1.5 py-0.5 mr-1">No BOM</span>}
                              {!r.live_sales_lookup_ok && <span className="text-[10px] font-bold text-[#B42318] bg-[#FEF3F2] border border-[#FDA29B] rounded px-1.5 py-0.5 mr-1" data-testid={`fg-consumption-monitor-fg-row-sales-error-${r.product_id}`}>Live Sales Lookup Failed</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
